import Button from 'components/base/Button';
import { Col, Form, Row, Modal } from 'react-bootstrap';
import ModalForm from 'components/forms/ModalForm';
import { ChangeEvent, FormEvent, useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  CREATE_JOB_STAGE,
  JobStageDTO,
  PATCH_JOB_STAGE
} from 'services/api/jobStage';
import { FormProps } from 'data/from-data/interface/modal-form-props.interface';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
export class JobStageFormData {
  [key: string]: FormProps;

  name: FormProps = {
    name: 'name',
    label: 'Name',
    type: 'text',
    placeholder: 'Name',
    required: true
  };
  description: FormProps = {
    name: 'description',
    label: 'Description',
    type: 'textarea',
    placeholder: 'Description',
    required: false
  };
}

interface JobStageModalProps {
  show: boolean;
  handleClose: () => void;
  isEditing?: boolean;
  jobStage?: any;
}

const JobStageModel = ({
  show,
  handleClose,
  isEditing = false,
  jobStage
}: JobStageModalProps) => {
  const [validated, setValidated] = useState<boolean>(false);
  const [createJobStage, setCreateJobStage] = useState<JobStageFormData>(
    new JobStageFormData()
  );
  const [isLoading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const queries = useQueryClient();
  const createStageAction = useMutation('createJobStage', CREATE_JOB_STAGE, {
    onError: () => {
      setLoading(false);
    },
    onSuccess: () => {
      setLoading(false);
      queries.invalidateQueries('get_jobStage');
      setCreateJobStage(new JobStageFormData());
      navigate('/home/apps/job-stage');
      setValidated(false);
    },
    onMutate: () => {
      setLoading(true);
    }
  });
  const updateJobStageAction = useMutation('updateJobStage', PATCH_JOB_STAGE, {
    onError: () => {
      setLoading(false);
    },
    onSuccess: () => {
      setLoading(false);
      queries.invalidateQueries('get_jobStage');
      setCreateJobStage(new JobStageFormData());
      navigate('/home/apps/job-stage');
    },
    onMutate: () => {
      setLoading(true);
    }
  });
  useEffect(() => {
    setCreateJobStage(prevData => {
      const updatedData = Object.keys(prevData).reduce((acc: any, key) => {
        acc[key] = {
          ...prevData[key],
          value: jobStage?.[key] || prevData[key].value
        };
        return acc;
      }, {});
      return updatedData;
    });
  }, [jobStage]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!createJobStage.name?.value) {
      event.stopPropagation();
      setValidated(true);
    } else {
      runFormSubmissionAction(event);
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setCreateJobStage(prevData => ({
      ...prevData,
      [name]: {
        ...prevData[name],
        value
      }
    }));
  };

  const runFormSubmissionAction = async (e: FormEvent) => {
    e.preventDefault();

    const {
      name: { value: name },
      description: { value: description }
    } = createJobStage;
    const payload: JobStageDTO = {
      ...createJobStage,
      name,
      description
    };

    isEditing
      ? await updateJobStageAction.mutateAsync({
          data: payload,
          jobId: (jobStage as any)?.id || jobStage?._id
        })
      : await createStageAction.mutateAsync(payload);
    handleClose();
  };
  const renderFormFields = () => {
    return Object.entries(createJobStage).map(([key, formProps]) => (
      <Col md="12" key={key}>
        <ModalForm
          formProps={formProps as unknown as FormProps}
          handleInputChange={handleInputChange}
        />
      </Col>
    ));
  };
  return (
    <Modal show={show} onHide={handleClose} className="p-0" centered>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Modal.Header className="border-200 p-4">
          <h5 className="modal-title text-1000 fs-6 lh-sm">
            {isEditing ? 'Edit Job Stage' : 'Create New Job Stage'}
          </h5>
          <Button className="p-1" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} className="fs-9" />
          </Button>
        </Modal.Header>
        <Modal.Body className="pt-4 pb-2 px-4 mb-5">
          <Row className="g-3 mb-8">{renderFormFields()}</Row>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end align-items-center px-4 pb-4 border-0 pt-3">
          <Button
            variant="phoenix-primary"
            size="sm"
            className="px-4 fs-10 my-0"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            className="px-9 fs-9 my-0"
            disabled={isLoading}
            type="submit"
            onClick={handleSubmit}
          >
            {isEditing ? 'Save Changes' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default JobStageModel;
